import Button from 'devextreme-react/button';
import TabPanel, { Item } from 'devextreme-react/tab-panel';
import { confirm } from 'devextreme/ui/dialog';
import { SelectionChangedEvent } from 'devextreme/ui/tab_panel';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { enableGrantModule } from '../../api/v2/GrantsApi';
import { getPhasesByGrant } from '../../api/v2/PhasesApi';
import { getDefaultModule, getDefaultModules } from '../../api/v2/TasklistsApi';
import { ModuleDto } from '../../dtos/ModuleDto';
import { Modules } from '../../dtos/Modules';
import '../css/Settings.css';
import { useAppContext } from '../shared/AppContext';
import './GrantDetailPhases.css';
import DCMain from './modules/dc/DCMain';
import PreAwardMain from './modules/pa/PreAward';
import CloseoutMain from './modules/co/Closeout';
import PriorApprovalMain from './modules/ap/PriorApproval';
import FederalOversightMain from './modules/fo/FederalOversight';
import { getGrantModules } from "../../api/v2/GrantsApi";
import { GRANT_MODULES_MAY_HAVE_CHANGED } from "../../api/v2/moduleEvents";

//TODO fix any
export default function GrantDetailPhases({ grantData, repopulateData }: any) {
    const app = useAppContext();

    const [selectedTabIndex, setSelectedTabIndex] = useState<number>(1);
    const [deepLinkHash, setDeepLinkHash] = useState<string | null>(null);
    const deepLinkSetTabRef = useRef<boolean>(false);

    const [preAwardEnabled, setPreAwardEnabled] = useState<boolean>(false);
    const [designContructionEnabled, setDesignContructionEnabled] = useState<boolean>(false);
    const [priorApprovalEnabled, setPriorApprovalEnabled] = useState<boolean>(false);
    const [closeoutEnabled, setCloseoutEnabled] = useState<boolean>(false);
    const [federalOversightEnabled, setFederalOversightEnabled] = useState<boolean>(false);

    // Track which modules have visible phases for this grant's status
    const [preAwardHasVisiblePhases, setPreAwardHasVisiblePhases] = useState<boolean>(false);
    const [priorApprovalHasVisiblePhases, setPriorApprovalHasVisiblePhases] = useState<boolean>(false);
    const [closeoutHasVisiblePhases, setCloseoutHasVisiblePhases] = useState<boolean>(false);
    const [federalOversightHasVisiblePhases, setFederalOversightHasVisiblePhases] = useState<boolean>(false);

    // Module names come from Defaults_Module, so tabs, toasts and prompts match what the templates call them.
    const [moduleNames, setModuleNames] = useState<Partial<Record<Modules, string>>>({});
    const moduleName = (moduleId: Modules) => moduleNames[moduleId] ?? '';

    useEffect(() => {
        const loadModuleNames = async () => {
            const result = await getDefaultModules();
            if (!result?.data) return;
            setModuleNames(Object.fromEntries(result.data.map(m => [m.id, m.name])));
        };
        loadModuleNames();
    }, []);

    // Opens whatever the current hash points at: selects the module's tab, then hands the task/subtask
    // part down for the module to expand and select.
    const applyDeepLinkHash = useCallback(() => {
        const rawHash = window.location.hash.replace('#', '');
        const hash = rawHash.toLowerCase();

        const moduleKey = Object.keys(moduleToTabIndex).find(key =>
            hash.startsWith(key.toLowerCase())
        );

        if (moduleKey) {
            const index = moduleToTabIndex[moduleKey];
            setSelectedTabIndex(index);
            deepLinkSetTabRef.current = true;

            const extraPart = rawHash.slice(moduleKey.length);

            if (extraPart.length > 0 && extraPart !== '-' && extraPart !== '/') {
                setDeepLinkHash(window.location.hash);
            }

            window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
        }
    }, []);

    // Run on mount for links arriving from elsewhere (email, the dashboard), and on hashchange for
    // links clicked while already on this page - the Progress Monitor's event tooltips set the hash.
    // The replaceState above clears the hash once applied, so re-setting it always fires a change.
    useEffect(() => {
        applyDeepLinkHash();
        window.addEventListener('hashchange', applyDeepLinkHash);
        return () => window.removeEventListener('hashchange', applyDeepLinkHash);
    }, [applyDeepLinkHash]);

    // Initialize module states when grant is loaded (runs once per grant)
    useEffect(() => {
        const initializeModules = async () => {
            if (!grantData?.id) return;

            const modules: ModuleDto[] = grantData?.modules ?? [];
            const paEnabled = modules.some(m => m.moduleId == Modules.PA && m.enabled);
            const dcEnabled = modules.some(m => m.moduleId == Modules.DC && m.enabled);
            const apEnabled = modules.some(m => m.moduleId == Modules.AP && m.enabled);
            const coEnabled = modules.some(m => m.moduleId == Modules.CO && m.enabled);
            const foEnabled = modules.some(m => m.moduleId == Modules.FO && m.enabled);

            setPreAwardEnabled(paEnabled);
            setPriorApprovalEnabled(apEnabled);
            setCloseoutEnabled(coEnabled);
            setFederalOversightEnabled(foEnabled);

            setDesignContructionEnabled(dcEnabled);

            // Only set default tab if deep link didn't already set it
            if (!deepLinkSetTabRef.current) {
                if (dcEnabled) {
                    setSelectedTabIndex(1);
                } else {
                    setSelectedTabIndex(0);
                }
            }

            // Check which modules have visible phases
            const hasVisiblePhases = async (moduleId: number) => {
                const module = await getDefaultModule(moduleId, grantData.id);
                const allPhaseTypes = module?.data?.phaseTypes ?? [];
                const nonDeletedTemplates = allPhaseTypes.filter(pt => !pt.deleted);
                if (nonDeletedTemplates.length > 0) return true;

                const isModuleEnabled = modules.some((m: ModuleDto) => m.moduleId === moduleId && m.enabled);
                if (isModuleEnabled) {
                    const phasesResult = await getPhasesByGrant(grantData.id, moduleId);
                    if (phasesResult?.data && phasesResult.data.length > 0) return true;
                }
                return false;
            };

            const paVisible = await hasVisiblePhases(Modules.PA);
            const apVisible = await hasVisiblePhases(Modules.AP);
            const coVisible = await hasVisiblePhases(Modules.CO);
            const foVisible = await hasVisiblePhases(Modules.FO);

            setPreAwardHasVisiblePhases(paVisible);
            setPriorApprovalHasVisiblePhases(apVisible);
            setCloseoutHasVisiblePhases(coVisible);
            setFederalOversightHasVisiblePhases(foVisible);
        };

        initializeModules();
    }, [grantData?.id]);

    // Read inside the listener, which is registered once per grant and would otherwise close over
    // the enabled flags as they were at subscribe time.
    const designContructionEnabledRef = useRef(false);
    const preAwardEnabledRef = useRef(false);
    const priorApprovalEnabledRef = useRef(false);
    const closeoutEnabledRef = useRef(false);
    const federalOversightEnabledRef = useRef(false);
    const moduleNamesRef = useRef(moduleNames);

    designContructionEnabledRef.current = designContructionEnabled;
    preAwardEnabledRef.current = preAwardEnabled;
    priorApprovalEnabledRef.current = priorApprovalEnabled;
    closeoutEnabledRef.current = closeoutEnabled;
    federalOversightEnabledRef.current = federalOversightEnabled;
    moduleNamesRef.current = moduleNames;

    // A trigger on another module's task can enable a module while the user is looking at this
    // page. Re-read just the enabled flags so the tab lights up on its own; the selected tab is
    // deliberately left alone so the user is not pulled away from what they were doing.
    useEffect(() => {
        const refreshModules = async () => {
            if (!grantData?.id) return;

            const result = await getGrantModules(grantData.id);
            const modules = result?.data;
            if (!modules) return;

            // A tab quietly un-greying is easy to miss, so say which module arrived.
            const announce = (name: string, wasEnabled: boolean, isEnabled: boolean) => {
                if (isEnabled && !wasEnabled) app.infoToast(`${name} module added to this grant.`);
            };

            const dcEnabled = modules.some(m => m.moduleId == Modules.DC && m.enabled);
            const paEnabled = modules.some(m => m.moduleId == Modules.PA && m.enabled);
            const apEnabled = modules.some(m => m.moduleId == Modules.AP && m.enabled);
            const coEnabled = modules.some(m => m.moduleId == Modules.CO && m.enabled);
            const foEnabled = modules.some(m => m.moduleId == Modules.FO && m.enabled);

            announce(moduleNamesRef.current[Modules.DC] ?? '', designContructionEnabledRef.current, dcEnabled);
            announce(moduleNamesRef.current[Modules.PA] ?? '', preAwardEnabledRef.current, paEnabled);
            announce(moduleNamesRef.current[Modules.AP] ?? '', priorApprovalEnabledRef.current, apEnabled);
            announce(moduleNamesRef.current[Modules.CO] ?? '', closeoutEnabledRef.current, coEnabled);
            announce(moduleNamesRef.current[Modules.FO] ?? '', federalOversightEnabledRef.current, foEnabled);

            // The page was loaded before the module existed, so it holds no phase data for it -
            // enabling the tab alone would open an empty one. Pull the grant's data again.
            const moduleAppeared =
                (dcEnabled && !designContructionEnabledRef.current) ||
                (paEnabled && !preAwardEnabledRef.current) ||
                (apEnabled && !priorApprovalEnabledRef.current) ||
                (coEnabled && !closeoutEnabledRef.current) ||
                (foEnabled && !federalOversightEnabledRef.current);

            if (moduleAppeared) await repopulateData();

            setPreAwardEnabled(paEnabled);
            setDesignContructionEnabled(dcEnabled);
            setPriorApprovalEnabled(apEnabled);
            setCloseoutEnabled(coEnabled);
            setFederalOversightEnabled(foEnabled);
        };

        window.addEventListener(GRANT_MODULES_MAY_HAVE_CHANGED, refreshModules);
        return () => window.removeEventListener(GRANT_MODULES_MAY_HAVE_CHANGED, refreshModules);
    }, [grantData?.id]);

    const onTabSelectionChanged = useCallback((e: SelectionChangedEvent) => {
        const newIndex = e.component.option("selectedIndex");
        setSelectedTabIndex(newIndex as number);
    }, []);


    const enableModule = async (
        moduleId: Modules,
        setEnabled: (v: boolean) => void,
        tabIndex: number
    ) => {
        const result = await confirm(
            `Are you sure you want to enable the ${moduleName(moduleId)} module for this grant?`,
            'Confirm'
        );

        if (result) {
            app.showLoadPanel();
            const results = await enableGrantModule(grantData?.id, moduleId);

            if (results?.error)
                app.errorToast(results.error.message);
            else {
                setEnabled(true);
                setSelectedTabIndex(tabIndex);
            }

            app.hideLoadPanel();
        }
    };

    const addPreAwardModule = () => enableModule(Modules.PA, setPreAwardEnabled, 0);
    const addPriorApprovalModule = () => enableModule(Modules.AP, setPriorApprovalEnabled, 2);
    const addCloseoutModule = () => enableModule(Modules.CO, setCloseoutEnabled, 3);
    const addFederalOversightModule = () => enableModule(Modules.FO, setFederalOversightEnabled, 4);

    const addPreAwardModuleEnabled = () => {
        if (app.UserIsPO || app.UserIsSuper) {
            return !preAwardEnabled && preAwardHasVisiblePhases;
        }
        else
            return false;
    }

    const addPriorApprovalModuleEnabled = () => {
        if (app.UserIsPO || app.UserIsSuper) {
            return !priorApprovalEnabled && designContructionEnabled && priorApprovalHasVisiblePhases;
        }
        else
            return false;
    }

    const addCloseoutModuleEnabled = () => {
        if (app.UserIsPO || app.UserIsSuper) {
            return !closeoutEnabled && designContructionEnabled && closeoutHasVisiblePhases;
        }
        else
            return false;
    }

    const addFederalOversightModuleEnabled = () => {
        if (app.UserIsPO || app.UserIsSuper) {
            return !federalOversightEnabled && designContructionEnabled && federalOversightHasVisiblePhases;
        }
        else
            return false;
    }

    const moduleToTabIndex: Record<string, number> = {
        pa: 0, // Pre-award
        dc: 1, // Design/Construction
        ap: 2, // Prior Approval
        co: 3, // Clouseout
        fo: 4  // Federal Oversight
    };

    const renderPreAwardTabWithButton = () => {
        const ref = (el: HTMLDivElement | null) => {
            if (el) {
                const parent = el.closest(".dx-item");
                if (parent && !parent.classList.contains("dx-item-pre-award-with-add-button")) {
                    parent.classList.add("dx-item-pre-award-with-add-button");
                }
            }
        };

        if (!preAwardEnabled) {
            return (
                <div id="module-approval-tab-with-add-button-0" className="module-approval-tab-with-add-button"
                    ref={ref}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <span className="dx-tab-text">{moduleName(Modules.PA)}</span>
                    {addPreAwardModuleEnabled() && (
                        <Button
                            icon="add"
                            hint={`Enable ${moduleName(Modules.PA)} Module`}
                            onClick={() => addPreAwardModule()}
                        />
                    )}
                </div>
            );
        } else {
            return (
                <div id="pre-award-tab"
                    onClick={(e) => setSelectedTabIndex(0)}
                >
                    <span className="dx-tab-text">{moduleName(Modules.PA)}</span>
                </div>
            );
        }
    };

    const renderPriorApprovalTabWithButton = () => {
        const ref = (el: HTMLDivElement | null) => {
            if (el) {
                const parent = el.closest(".dx-item");
                if (parent && !parent.classList.contains("dx-item-prior-approval-with-add-button")) {
                    parent.classList.add("dx-item-prior-approval-with-add-button");
                }
            }
        };

        if (!priorApprovalEnabled) {
            return (
                <div id="module-approval-tab-with-add-button-2" className="module-approval-tab-with-add-button"
                    ref={ref}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <span className="dx-tab-text">{moduleName(Modules.AP)}</span>
                    {addPriorApprovalModuleEnabled() && (
                        <Button
                            icon="add"
                            hint={`Enable ${moduleName(Modules.AP)} Module`}
                            onClick={() => addPriorApprovalModule()}
                        />
                    )}
                </div>
            );
        } else {
            return (
                <div id="prior-approval-tab"
                    onClick={(e) => setSelectedTabIndex(2)}
                >
                    <span className="dx-tab-text">{moduleName(Modules.AP)}</span>
                </div>
            );
        }
    };

    const renderCloseoutTabWithButton = () => {
        const ref = (el: HTMLDivElement | null) => {
            if (el) {
                const parent = el.closest(".dx-item");
                if (parent && !parent.classList.contains("dx-item-closeout-with-add-button")) {
                    parent.classList.add("dx-item-closeout-with-add-button");
                }
            }
        };

        if (!closeoutEnabled) {
            return (
                <div id="module-approval-tab-with-add-button-3" className="module-approval-tab-with-add-button"
                    ref={ref}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <span className="dx-tab-text">{moduleName(Modules.CO)}</span>
                    {addCloseoutModuleEnabled() && (
                        <Button
                            icon="add"
                            hint={`Enable ${moduleName(Modules.CO)} Module`}
                            onClick={() => addCloseoutModule()}
                        />
                    )}
                </div>
            );
        } else {
            return (
                <div id="closeout-tab"
                    onClick={(e) => setSelectedTabIndex(3)}
                >
                    <span className="dx-tab-text">{moduleName(Modules.CO)}</span>
                </div>
            );
        }
    };

    const renderFederalOversightTabWithButton = () => {
        const ref = (el: HTMLDivElement | null) => {
            if (el) {
                const parent = el.closest(".dx-item");
                if (parent && !parent.classList.contains("dx-item-federal-oversight-with-add-button")) {
                    parent.classList.add("dx-item-federal-oversight-with-add-button");
                }
            }
        };

        if (!federalOversightEnabled) {
            return (
                <div id="module-approval-tab-with-add-button-4" className="module-approval-tab-with-add-button"
                    ref={ref}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <span className="dx-tab-text">{moduleName(Modules.FO)}</span>
                    {addFederalOversightModuleEnabled() && (
                        <Button
                            icon="add"
                            hint={`Enable ${moduleName(Modules.FO)} Module`}
                            onClick={() => addFederalOversightModule()}
                        />
                    )}
                </div>
            );
        } else {
            return (
                <div id="federal-oversight-tab"
                    onClick={(e) => setSelectedTabIndex(4)}
                >
                    <span className="dx-tab-text">{moduleName(Modules.FO)}</span>
                </div>
            );
        }
    };

    if (!grantData?.id) {
        return null;
    } else {
        return (
            <>
                <TabPanel
                    id="grant-detail-phases"
                    style={{ marginTop: 5 }}
                    selectedIndex={selectedTabIndex}
                    onSelectionChanged={onTabSelectionChanged}
                >

                    <Item tabRender={renderPreAwardTabWithButton}>
                        {preAwardEnabled && (
                            <PreAwardMain grantData={grantData} deepLinkHash={deepLinkHash} />
                        )}
                    </Item>

                    <Item disabled={!designContructionEnabled} title={moduleName(Modules.DC)}>
                        {designContructionEnabled && (
                            <div id="grantVisualizationsPanelSmaller" >
                                <DCMain grantData={grantData} repopulateData={repopulateData} deepLinkHash={deepLinkHash} />
                            </div>
                        )}
                    </Item>

                    <Item tabRender={renderPriorApprovalTabWithButton}>
                        {priorApprovalEnabled && (
                            <PriorApprovalMain grantData={grantData} deepLinkHash={deepLinkHash} />
                        )}
                    </Item>

                    <Item tabRender={renderCloseoutTabWithButton}>
                        {closeoutEnabled && (
                            <CloseoutMain grantData={grantData} deepLinkHash={deepLinkHash} />
                        )}
                    </Item>

                    <Item tabRender={renderFederalOversightTabWithButton}>
                        {federalOversightEnabled && (
                            <FederalOversightMain grantData={grantData} deepLinkHash={deepLinkHash} />
                        )}
                    </Item>

                </TabPanel>
            </>
        );
    }
}
